import {
  AxiosResponseAndError,
  ResponseType,
} from "../types/axiosResponseType";
import { QUERY_KEYS } from "./querykeys";
import ApiRequest from "../ApiRequest";
import { requestURLs } from "@/Environment";
import { useQuery } from "@tanstack/react-query";
import {
  OrgStatusEnumValue,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import { TaskColorPaletteEnum } from "@backend/src/schemas/userSchema";
import {
  OrgListOfNonWorkingDaysEnum,
  type TimesheetEmailNotifications,
} from "@backend/src/schemas/organisationSchema";
import { severity } from "../mutation/useOrganisationMutation";


export type UserOrganisationType = {
  userOrganisationId: string;
  jobTitle?: string;
  role?: keyof typeof UserRoleEnumValue;
  taskColour?: TaskColorPaletteEnum;
  user: {
    userId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarImg?: string;
    deletedAt?: string;
    isVerified: boolean;
    status: string;
    countryCode: string;
    phoneNumber: string;
    createdByUserId: string;
    userOrganisation?: UserOrganisationType[];
    isActive: boolean;
    userAvailability: UserAvailability[];
  };
  teamUnitId?: string | null;
  teamUnit?: { teamUnitId: string; name: string } | null;
  orgUnitId?: string | null;
  orgUnit?: { teamUnitId: string; name: string } | null;
  jobTitleId?: string | null;
  jobTitleRelation?: { jobTitleId: string; title: string } | null;
  contractTypeId?: string | null;
  contractType?: { contractTypeId: string; name: string } | null;
  resourceType?: "INTERNAL" | "EXTERNAL" | null;
  contractualCapacity?: number | null;
  dailyCapacity?: number | null;
  managerId?: string | null;
  manager?: { userId: string; firstName?: string; lastName?: string; email: string } | null;
  startDate?: string | null;
  endDate?: string | null;
};

export interface UserAvailability {
  userAvailabilityId: string
  userId: string
  reason: string
  startDate: string
  endDate: string
  createdAt: string
  updatedAt: string
}
export type OrganisationResponseType = ResponseType<{
  organisationId: string;
  organisationName: string;
  industry: string;
  status: keyof typeof OrgStatusEnumValue;
  country: string;
  nonWorkingDays: OrgListOfNonWorkingDaysEnum[];
  createdAt: Date;
  updatedAt: Date;
  tenantId: string;
  createdByUserId: string;
  userOrganisation: UserOrganisationType[];
  holidayCsvUrl: string | null;
  jobTitlesOfOrg: string[];
  riskType: string[];
  severityTypes: severity[];
  onTimeDelayMax: number | null;

  moderateDelayMax: number | null;
  onBudgetMax: number | null;
  moderateBudgetMax: number | null;
  timesheetMaxHoursPerDay: number | null;
  timesheetEmailNotifications: TimesheetEmailNotifications;
  phoneNumber: string | null;
  countryCode: string | null;
  orgHolidays?: import("../mutation/useOrganisationMutation").OrgHolidays[];
}>;



function useOrganisationDetailsQuery(organisationId: string,enabled=true) {
  return useQuery<
    AxiosResponseAndError<OrganisationResponseType>["response"],
    AxiosResponseAndError<OrganisationResponseType>["error"]
  >({
    queryKey: [QUERY_KEYS.organisation, organisationId],
    queryFn: async () =>
      await ApiRequest.get(`${requestURLs.organisation}/${organisationId}`),
    enabled: enabled && Boolean(organisationId),
    staleTime: 5 * 60 * 1000, // 5 min – avoid repeated calls when multiple consumers use same org
    refetchOnWindowFocus: false,
  });
}

export default useOrganisationDetailsQuery;
